import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { History, Eye, User, Phone, X } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const AuditHistory = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [selectedChanges, setSelectedChanges] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(apiEndPointsConfig.getAuditHistory);
      if (response.data.success) {
        setHistory(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatChanges = (changes) => {
    if (!changes) return "No data";
    const before = changes.before || {};
    const after = changes.after || {};
    
    // Find keys that are different
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    const differences = [];
    
    keys.forEach(key => {
      if (key === "_id" || key === "__v" || key === "updatedAt" || key === "createdAt" || key === "password") return;
      
      const valBefore = before[key];
      const valAfter = after[key];
      
      if (JSON.stringify(valBefore) !== JSON.stringify(valAfter)) {
        differences.push({
          field: key,
          old: valBefore !== undefined ? String(valBefore) : "N/A",
          new: valAfter !== undefined ? String(valAfter) : "N/A"
        });
      }
    });
    
    return differences;
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
        <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, color: theme.palette.primary.main }}>
          <History size={24} />
        </Box>
        <Typography variant="h5" fontWeight={700}>
          Admin Audit History
        </Typography>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Admin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length > 0 ? (
                history.map((log) => (
                  <TableRow key={log._id} sx={{ "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.01) } }}>
                    <TableCell>
                      <Typography variant="body2">{new Date(log.timestamp).toLocaleDateString()}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(log.timestamp).toLocaleTimeString()}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <User size={14} color={theme.palette.text.secondary} />
                        <Typography variant="body2" fontWeight={600}>{log.adminName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.action} 
                        size="small" 
                        color={log.action.includes("ADD") ? "success" : log.action.includes("EDIT") ? "info" : "primary"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {log.module === "DID" ? <Phone size={14} /> : <User size={14} />}
                        <Typography variant="body2">{log.module}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{log.targetName || "N/A"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<Eye size={14} />}
                        onClick={() => setSelectedChanges(formatChanges(log.changes))}
                        sx={{ textTransform: "none" }}
                      >
                        View Changes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">No history found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Changes Dialog */}
      <Dialog 
        open={Boolean(selectedChanges)} 
        onClose={() => setSelectedChanges(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700}>Modification Details</Typography>
          <IconButton onClick={() => setSelectedChanges(null)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedChanges && selectedChanges.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Before</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedChanges.map((diff, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>{diff.field}</TableCell>
                    <TableCell sx={{ color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.05) }}>{diff.old}</TableCell>
                    <TableCell sx={{ color: "success.main", bgcolor: alpha(theme.palette.success.main, 0.05) }}>{diff.new}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography align="center" sx={{ py: 3 }} color="text.secondary">
              No significant field changes detected.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuditHistory;
